import React, { useMemo, useState } from 'react';
import { Button, Icon, Input, useGraphQLQuery, useGraphQLMutation } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import {
    PageContainer,
    IllustrationSection,
    IllustrationScaler,
    IllustrationCaption,
    FormSection,
    FormContainer,
    LogoContainer,
    WelcomeText,
    OptionsRow,
    CheckboxLabel,
    ForgotPasswordLink,
    SignUpText,
    ErrorText
} from './styles';
import { CurrentUser } from '../../context/UserContext';
import { TviklaHubDiagram } from '../LandingPage/TviklaHubDiagram';

type AdminAccessLoginUser = {
  id: number;
  contactId?: number | null;
  customerId?: number | null;
  vendorId?: number | null;
  agentId?: number | null;
  name: string;
  email: string;
  role: string;
  status: string;
  scope: string;
  preferredLanguage?: string;
};

type AdminAccessLoginRole = {
  role: string;
  label: string;
};

type LoginUsersQueryData = {
  adminAccessSnapshot: {
    users: AdminAccessLoginUser[];
    roles: AdminAccessLoginRole[];
  };
};

type LoginUsersQueryVariables = {
  limit: number;
  offset: number;
  auditLimit: number;
};

type LoginMutationData = {
  login: {
    token: string;
    user: AdminAccessLoginUser;
  };
};

type LoginMutationVariables = {
  email: string;
};

const LOGIN_MUTATION = gql`
  mutation Login($email: String!) {
    login(email: $email) {
      token
      user {
        id
        contactId
        customerId
        vendorId
        agentId
        name
        email
        role
        status
        scope
        preferredLanguage
      }
    }
  }
`;

const LOGIN_USERS_QUERY = gql`
  query LoginUsers($limit: Int!, $offset: Int!, $auditLimit: Int!) {
    adminAccessSnapshot(limit: $limit, offset: $offset, auditLimit: $auditLimit) {
      users {
        id
        contactId
        customerId
        vendorId
        agentId
        name
        email
        role
        status
        scope
        preferredLanguage
      }
      roles {
        role
        label
      }
    }
  }
`;

interface LoginPageProps {
    onLogin: (user: CurrentUser) => void;
    onSignUp: () => void;
}

const ROLE_LABEL_FALLBACK: Record<string, string> = {
  admin: 'Admin – Business Owner',
  ops: 'Ops – Logistics / Execution',
  compliance: 'Compliance – Customs & Docs',
  finance: 'Finance – Payments & Invoices',
  sales: 'Sales – Front-office',
  agent: 'Agent – Tvikla Authorized',
  customer: 'Customer – Buyer',
  supplier: 'Supplier – Seller/Manufacturer',
};

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin, onSignUp }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [error, setError] = useState('');

    const { data } = useGraphQLQuery<LoginUsersQueryData, LoginUsersQueryVariables>(LOGIN_USERS_QUERY, {
      variables: { limit: 100, offset: 0, auditLimit: 0 },
      showErrorToast: false,
    });

    const [login, { loading: loggingIn }] = useGraphQLMutation<LoginMutationData, LoginMutationVariables>(LOGIN_MUTATION, {
      showSuccessToast: false,
      showErrorToast: false,
    });

    const roleLabels = useMemo(() => {
      const labels = new Map<string, string>();
      (data?.adminAccessSnapshot?.roles || []).forEach((role) => {
        labels.set(role.role.toLowerCase(), role.label);
      });
      return labels;
    }, [data]);

    const handleLogin = async () => {
      setError('');
      setEmailError(false);

      if (!email) {
        setEmailError(true);
        setError('Please enter your email');
        return;
      }

      try {
        const result = await login({ variables: { email: email.trim().toLowerCase() } });
        const payload = result.data?.login;
        if (!payload) {
          setError('Invalid email or password');
          return;
        }

        localStorage.setItem('token', payload.token);
        const normalizedRole = payload.user.role.toLowerCase();
        onLogin({
          id: String(payload.user.id),
          contactId: payload.user.contactId ?? undefined,
          customerId: payload.user.customerId ?? undefined,
          vendorId: payload.user.vendorId ?? undefined,
          agentId: payload.user.agentId ?? undefined,
          name: payload.user.name,
          email: payload.user.email,
          role: roleLabels.get(normalizedRole) || ROLE_LABEL_FALLBACK[normalizedRole] || payload.user.role,
          scope: payload.user.scope,
          status: payload.user.status,
          preferredLanguage: payload.user.preferredLanguage || 'en',
        });
      } catch {
        setError('Invalid email or password');
      }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleLogin();
      }
    };

    return (
        <PageContainer>
            <IllustrationSection>
                <IllustrationScaler>
                    <TviklaHubDiagram />
                </IllustrationScaler>
                <IllustrationCaption>
                    <Icon name="Globe" size={16} color="#c94e0c" />
                    Tvikla OS &middot; Export Platform
                </IllustrationCaption>
            </IllustrationSection>
            <FormSection>
                <FormContainer>
                    <LogoContainer>
                        <img src="/tviklax-logo.png" alt="TviklaX" />
                        <WelcomeText>Welcome to Tvikla</WelcomeText>
                    </LogoContainer>

                    <Input
                        type="email"
                        label="Email"
                        value={email}
                        onChange={(e: any) => {
                          setEmail(e.target.value);
                          setEmailError(false);
                        }}
                        error={emailError ? "Please enter your email" : undefined}
                        endAdornment={
                            emailError ? (
                                <Icon name="AlertCircle" size={18} style={{ color: '#EF4444' }} />
                            ) : (
                                <Icon name="Mail" size={18} style={{ color: '#F97316' }} />
                            )
                        }
                    />

                    <Input
                        type={showPassword ? "text" : "password"}
                        label="Password"
                        value={password}
                        onChange={(e: any) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        onKeyPress={handleKeyPress}
                        endAdornment={
                            <div
                                style={{ cursor: 'pointer', color: '#F97316', display: 'flex', alignItems: 'center' }}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <Icon name="EyeOff" size={18} /> : <Icon name="Eye" size={18} />}
                            </div>
                        }
                    />

                    {error && <ErrorText>{error}</ErrorText>}

                    <OptionsRow>
                        <CheckboxLabel>
                            <input type="checkbox" />
                            Remember me
                        </CheckboxLabel>
                        <ForgotPasswordLink>Forgot Password ?</ForgotPasswordLink>
                    </OptionsRow>

                    <Button onClick={handleLogin} disabled={loggingIn} style={{ width: '100%' }}>
                        <Icon name="Lock" size={18} color="white" />
                        {loggingIn ? 'Signing in…' : 'Secure Login'}
                    </Button>

                    <SignUpText>
                        New to Tvikla? <span onClick={onSignUp}>Sign Up</span>
                    </SignUpText>
                </FormContainer>
            </FormSection>
        </PageContainer>
    );
};
